// MarkupPopup.tsx
//
// MarkupPopup defines content for a popup that displays map markup.
// If the user changes the markup, it will reformat the map.

import { useMapStoreContext } from 'app/src/editor/MapStoreContext';
import { useUndoRedoContext } from 'app/src/editor/UndoRedoContext';
import LevelDataBuilder from 'app/src/LevelDataBuilder';
import LevelParser from 'app/src/LevelParser';
import Modal from 'app/src/modals/Modal';
import * as React from 'react';

type Props = {
	onClose: () => void;
	onError: (message: string, details: string) => void;
};

export default function MarkupPopup({ onClose, onError }: Props): React.ReactElement {
	const { addUndoState } = useUndoRedoContext();
	const { mapStore } = useMapStoreContext();

	const levelData = LevelDataBuilder.sharedInstance.build(mapStore.buildLevelDescription());
	const markup = JSON.stringify(levelData, null, 2);

	const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

	React.useEffect(() => {
		if (textareaRef.current) {
			textareaRef.current.focus();
			textareaRef.current.select();
		}
	}, []);

	const handleSaveClick = (): void => {
		if (!textareaRef.current) {
			return;
		}

		try {
			const newLevelData = JSON.parse(textareaRef.current.value);
			const newLevelDescription = LevelParser.sharedInstance.parse(newLevelData);
			mapStore.setLevelDescription(newLevelDescription);

			addUndoState();
		} catch (error) {
			onError('There was an error importing markup:', error.message);
		}

		onClose();
	};

	return (
		<Modal className="MarkupPopup">
			<textarea className="MarkupPopup-textarea" defaultValue={markup} ref={textareaRef} />
			<div className="MarkupPopup-controls">
				<button
					className="MarkupPopup-control MarkupPopup-control--cancel"
					onClick={(): void => onClose()}
				>
					Cancel
				</button>
				<button
					className="MarkupPopup-control MarkupPopup-control--save"
					onClick={(): void => handleSaveClick()}
				>
					Save
				</button>
			</div>
		</Modal>
	);
}
